import { getSourceUUID } from "@mirage/shared/compose/compose-session";

import type {
  ChatConversationMessage,
  ChatConversationMessageMessage,
  ChatSource,
} from "@mirage/mosaics/Chat/types";

/**
 * @returns a map of source UUID to source for all sources referenced in the messages.
 */
export function getAllReferencedSources(messages: ChatConversationMessage[]) {
  const sources = new Map<string, ChatSource>();
  for (const message of messages) {
    if (message.type !== "message") {
      continue;
    }
    for (const source of message.referencingSources || []) {
      const sourceUUID = getSourceUUID(source);
      if (sourceUUID) {
        sources.set(sourceUUID, source);
      }
    }
  }
  return sources;
}

export function getUnreferencedSources(
  messages: ChatConversationMessage[],
  sources: ChatSource[],
) {
  const referencedSources = getAllReferencedSources(messages);
  return sources.filter((source) => {
    const sourceUUID = getSourceUUID(source);
    return sourceUUID && !referencedSources.has(sourceUUID);
  });
}

/**
 * @returns an object containing 'citations' and 'context' source arrays.
 * If 'referencingSources' exist, they are returned in 'citations', and 'context' is empty.
 * Otherwise, 'additionalContext' sources are returned in 'context', and 'citations' is empty.
 * Sources within each array are de-duplicated.
 */
export function getCitationAndContextSources(
  message: ChatConversationMessageMessage,
): { citations: ChatSource[]; context: ChatSource[] } {
  const citationsMap = new Map<string, ChatSource>();
  const contextMap = new Map<string, ChatSource>();

  if (message.referencingSources && message.referencingSources.length > 0) {
    for (const citation of message.referencingSources) {
      const sourceUUID = getSourceUUID(citation);
      if (sourceUUID && !citationsMap.has(sourceUUID)) {
        citationsMap.set(sourceUUID, citation);
      }
    }
  } else if (
    message.additionalContext &&
    message.additionalContext.length > 0
  ) {
    for (const contextSource of message.additionalContext) {
      const sourceUUID = getSourceUUID(contextSource);
      if (sourceUUID && !contextMap.has(sourceUUID)) {
        contextMap.set(sourceUUID, contextSource);
      }
    }
  }

  return {
    citations: Array.from(citationsMap.values()),
    context: Array.from(contextMap.values()),
  };
}
